import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2, X, Shield, UserCheck, UserX, Key, Search, Eye, FileText, Gift, ChevronRight, Settings, BarChart3, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { userManagementAPI, casesAPI, referralAPI } from '../utils/api';
import api from '../utils/api';

const ROLES = [
  { value: 'client', label: 'Client', color: 'bg-blue-100 text-blue-700' },
  { value: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-700' },
  { value: 'super_admin', label: 'Super Admin', color: 'bg-red-100 text-red-700' }
];

const UserManagement = ({ canDelete = true }) => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({ cases: [], referrals: [], loading: true });
  
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    company_name: '',
    phone: '',
    password: '',
    role: 'client'
  });
  const [newPassword, setNewPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // System metrics state
  const [systemMetrics, setSystemMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  useEffect(() => {
    loadUsers();
    loadSystemMetrics();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await userManagementAPI.getAllUsers();
      setUsers(response.data.users);
      setStats(response.data.stats);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Super Admin access required');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSystemMetrics = async () => {
    setMetricsLoading(true);
    try {
      const response = await api.get('/admin/system-metrics');
      setSystemMetrics(response.data);
    } catch (error) {
      console.error('Failed to load system metrics:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadUserDetails = async (user) => {
    setSelectedUser(user);
    setUserDetails({ cases: [], referrals: [], loading: true });
    setShowUserDetailsModal(true);
    
    try {
      // Load user's cases and referrals
      const [casesRes, referralsRes] = await Promise.all([
        api.get(`/users/${user.id}/cases`),
        api.get(`/users/${user.id}/referrals`)
      ]);
      
      setUserDetails({
        cases: casesRes.data || [],
        referrals: referralsRes.data || [],
        loading: false
      });
    } catch (error) {
      console.error('Failed to load user details:', error);
      setUserDetails({ cases: [], referrals: [], loading: false });
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.full_name || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setActionLoading(true);
    try {
      await userManagementAPI.createUser(newUser);
      toast.success('User created successfully');
      setShowCreateModal(false);
      setNewUser({ email: '', full_name: '', company_name: '', phone: '', password: '', role: 'client' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      await userManagementAPI.updateUser(selectedUser.id, {
        full_name: selectedUser.full_name,
        company_name: selectedUser.company_name,
        phone: selectedUser.phone,
        role: selectedUser.role,
        is_active: selectedUser.is_active
      });
      toast.success('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) return;
    
    try {
      await userManagementAPI.deleteUser(userId);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    setActionLoading(true);
    try {
      await userManagementAPI.resetPassword(selectedUser.id, newPassword);
      toast.success('Password reset successfully');
      setShowResetPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      await userManagementAPI.updateUser(user.id, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLES.find(r => r.value === role) || ROLES[0];
    return (
      <span className={`text-xs px-2 py-1 rounded-full font-medium ${roleConfig.color}`}>
        {roleConfig.label}
      </span>
    );
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading users...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Super Admin Control Panel Header */}
      <div className="bg-gradient-to-r from-red-600 to-purple-600 text-white rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Shield size={28} />
              Super Admin Control Panel
            </h2>
            <p className="text-red-100 mt-1">Full system control - manage users, cases, metrics and all platform data</p>
          </div>
          <button
            onClick={() => { loadUsers(); loadSystemMetrics(); }}
            className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <RefreshCw size={18} />
            Refresh All
          </button>
        </div>
      </div>

      {/* System Metrics Dashboard */}
      {systemMetrics && (
        <div className="mb-6 space-y-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 size={20} className="text-purple-600" />
            System Metrics & Analytics
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {/* Financial Metrics */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <div className="text-xl font-bold text-green-700">${((systemMetrics.financials?.total_frozen || 0) / 1000).toFixed(0)}K</div>
              <div className="text-xs text-green-600">Total Frozen</div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4">
              <div className="text-xl font-bold text-emerald-700">${((systemMetrics.financials?.resolved_recovered || 0) / 1000).toFixed(0)}K</div>
              <div className="text-xs text-emerald-600">Recovered</div>
            </div>
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-4">
              <div className="text-xl font-bold text-cyan-700">{systemMetrics.financials?.recovery_rate || 0}%</div>
              <div className="text-xs text-cyan-600">Recovery Rate</div>
            </div>
            
            {/* Case Metrics */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <div className="text-xl font-bold text-blue-700">{systemMetrics.cases?.total || 0}</div>
              <div className="text-xs text-blue-600">Total Cases</div>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
              <div className="text-xl font-bold text-amber-700">{systemMetrics.cases?.active || 0}</div>
              <div className="text-xs text-amber-600">Active Cases</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
              <div className="text-xl font-bold text-purple-700">{systemMetrics.referrals?.total || 0}</div>
              <div className="text-xs text-purple-600">Referrals</div>
            </div>
          </div>
          
          {/* Commission & Operations Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-lg font-bold text-slate-900">${((systemMetrics.referrals?.potential_commission || 0) / 1000).toFixed(1)}K</div>
              <div className="text-xs text-slate-500">Potential Commission</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-lg font-bold text-slate-900">${((systemMetrics.referrals?.paid_commission || 0) / 1000).toFixed(1)}K</div>
              <div className="text-xs text-slate-500">Paid Commission</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-lg font-bold text-slate-900">{systemMetrics.operations?.total_documents || 0}</div>
              <div className="text-xs text-slate-500">Documents</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-lg font-bold text-slate-900">{systemMetrics.operations?.task_completion_rate || 0}%</div>
              <div className="text-xs text-slate-500">Task Completion</div>
            </div>
          </div>
        </div>
      )}

      {/* User Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
            <div className="text-sm text-slate-500">Total Users</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.clients}</div>
            <div className="text-sm text-blue-600">Clients</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-700">{stats.admins}</div>
            <div className="text-sm text-purple-600">Admins</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-700">{stats.super_admins}</div>
            <div className="text-sm text-red-600">Super Admins</div>
          </div>
        </div>
      )}

      {/* Header with Search and Filters */}
      <div className="flex justify-between items-center gap-4">
        <div className="flex-1 flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
          >
            <option value="all">All Roles</option>
            <option value="client">Clients</option>
            <option value="admin">Admins</option>
            <option value="super_admin">Super Admins</option>
          </select>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="primary-button flex items-center gap-2"
        >
          <Plus size={18} />
          Add User
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">User</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Role</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Created</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Last Login</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                  No users found
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-slate-900">{user.full_name}</div>
                      <div className="text-sm text-slate-500">{user.email}</div>
                      {user.company_name && (
                        <div className="text-xs text-slate-400">{user.company_name}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">{getRoleBadge(user.role)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleUserStatus(user)}
                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                        user.is_active !== false
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {user.is_active !== false ? <UserCheck size={12} /> : <UserX size={12} />}
                      {user.is_active !== false ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {user.last_login ? format(new Date(user.last_login), 'MMM d, h:mm a') : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => loadUserDetails(user)}
                        className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="View user details, cases & referrals"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => { setSelectedUser(user); setShowEditModal(true); }}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit user"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => { setSelectedUser(user); setShowResetPasswordModal(true); }}
                        className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Reset password"
                      >
                        <Key size={16} />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => handleDeleteUser(user.id, user.full_name)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete user"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900">Create New User</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                  <input
                    type="text"
                    value={newUser.company_name}
                    onChange={(e) => setNewUser({ ...newUser, company_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Password *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Role *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                >
                  {ROLES.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 primary-button py-2" disabled={actionLoading}>
                  {actionLoading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900">Edit User</h3>
              <button onClick={() => { setShowEditModal(false); setSelectedUser(null); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleUpdateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={selectedUser.email}
                  disabled
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={selectedUser.full_name || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, full_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                  <input
                    type="text"
                    value={selectedUser.company_name || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, company_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={selectedUser.phone || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Role</label>
                <select
                  value={selectedUser.role}
                  onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                >
                  {ROLES.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={selectedUser.is_active !== false}
                  onChange={(e) => setSelectedUser({ ...selectedUser, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="is_active" className="text-sm text-slate-700">Account is active</label>
              </div>
              
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedUser(null); }}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 primary-button py-2" disabled={actionLoading}>
                  {actionLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900">Reset Password</h3>
              <button onClick={() => { setShowResetPasswordModal(false); setSelectedUser(null); setNewPassword(''); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleResetPassword} className="p-6 space-y-4">
              <p className="text-sm text-slate-600">
                Reset password for <strong>{selectedUser.full_name}</strong> ({selectedUser.email})
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">New Password *</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  required
                  minLength={6}
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowResetPasswordModal(false); setSelectedUser(null); setNewPassword(''); }}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 primary-button py-2" disabled={actionLoading}>
                  {actionLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Details Modal with Cases & Referrals */}
      {showUserDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-lg font-bold text-slate-900">User Details</h3>
                <p className="text-sm text-slate-600">{selectedUser.full_name} ({selectedUser.email})</p>
              </div>
              <button onClick={() => { setShowUserDetailsModal(false); setSelectedUser(null); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* User Info Card */}
              <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-slate-600">Full Name</div>
                    <div className="font-semibold text-slate-900">{selectedUser.full_name || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-slate-600">Email</div>
                    <div className="font-semibold text-slate-900">{selectedUser.email}</div>
                  </div>
                  <div>
                    <div className="text-slate-600">Company</div>
                    <div className="font-semibold text-slate-900">{selectedUser.company_name || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-slate-600">Role</div>
                    <div>{getRoleBadge(selectedUser.role)}</div>
                  </div>
                  <div>
                    <div className="text-slate-600">Phone</div>
                    <div className="font-semibold text-slate-900">{selectedUser.phone || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-slate-600">Status</div>
                    <div className={`font-semibold ${selectedUser.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedUser.is_active !== false ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-600">Created</div>
                    <div className="font-semibold text-slate-900">
                      {selectedUser.created_at ? format(new Date(selectedUser.created_at), 'MMM d, yyyy') : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-600">Last Login</div>
                    <div className="font-semibold text-slate-900">
                      {selectedUser.last_login ? format(new Date(selectedUser.last_login), 'MMM d, h:mm a') : 'Never'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => { setShowUserDetailsModal(false); setShowEditModal(true); }}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-2"
                >
                  <Edit2 size={16} /> Edit User
                </button>
                <button
                  onClick={() => { setShowUserDetailsModal(false); setShowResetPasswordModal(true); }}
                  className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors flex items-center gap-2"
                >
                  <Key size={16} /> Reset Password
                </button>
                <button
                  onClick={() => toggleUserStatus(selectedUser)}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    selectedUser.is_active !== false 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {selectedUser.is_active !== false ? <UserX size={16} /> : <UserCheck size={16} />}
                  {selectedUser.is_active !== false ? 'Deactivate' : 'Activate'}
                </button>
              </div>

              {/* Cases Section */}
              <div>
                <h4 className="text-md font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText size={18} className="text-purple-600" />
                  User's Cases ({userDetails.loading ? '...' : userDetails.cases.length})
                </h4>
                
                {userDetails.loading ? (
                  <div className="text-center py-4 text-slate-600">Loading cases...</div>
                ) : userDetails.cases.length === 0 ? (
                  <div className="text-center py-4 text-slate-500 bg-slate-50 rounded-lg">No cases found for this user</div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {userDetails.cases.map((caseItem) => (
                      <div key={caseItem.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium text-slate-900">{caseItem.company_name}</div>
                            <div className="text-sm text-slate-500">
                              Processor: {caseItem.processor_name} | Frozen: ${(caseItem.frozen_amount || 0).toLocaleString()}
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            caseItem.status === 'resolved' ? 'bg-green-100 text-green-700' :
                            caseItem.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {caseItem.status?.replace('_', ' ') || 'pending'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Referrals Section */}
              <div>
                <h4 className="text-md font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <Gift size={18} className="text-cyan-600" />
                  User's Referrals ({userDetails.loading ? '...' : userDetails.referrals.length})
                </h4>
                
                {userDetails.loading ? (
                  <div className="text-center py-4 text-slate-600">Loading referrals...</div>
                ) : userDetails.referrals.length === 0 ? (
                  <div className="text-center py-4 text-slate-500 bg-slate-50 rounded-lg">No referrals found for this user</div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {userDetails.referrals.map((referral) => (
                      <div key={referral.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium text-slate-900">{referral.merchant_company_name || referral.merchant_contact_name}</div>
                            <div className="text-sm text-slate-500">
                              Reserves: ${(referral.held_reserves_amount || 0).toLocaleString()} | 
                              Commission: ${((referral.held_reserves_amount || 0) * 0.1).toLocaleString()}
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            referral.status === 'paid' ? 'bg-green-100 text-green-700' :
                            referral.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                            referral.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {referral.status || 'pending'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
